import React from 'react';
import type { AuditTimelineEntry } from '../../types/acknowledgementAuditTrail.types';
import {
  formatAuditTimestamp,
  formatTimelineEventTitle,
} from '../../utils/acknowledgementAuditTrailUtils';
import { simplifyEscalationEventMessage } from '../../utils/criticalNotificationUtils';

interface AuditTrailTimelineProps {
  events: AuditTimelineEntry[];
  emptyMessage?: string;
}

function formatTimelineMessage(event: AuditTimelineEntry): string {
  if (event.reason?.trim()) {
    return simplifyEscalationEventMessage(event.reason);
  }

  return event.displayStatus || event.deliveryStatus || event.actor || '—';
}

export default function AuditTrailTimeline({
  events,
  emptyMessage = 'No audit events recorded yet.',
}: Readonly<AuditTrailTimelineProps>) {
  if (!events.length) {
    return <p className="esc-monitor-placeholder">{emptyMessage}</p>;
  }

  return (
    <ol className="esc-monitor-timeline aat-timeline">
      {events.map(event => (
        <li
          key={event.id}
          className="esc-monitor-timeline-item"
        >
          <div
            className={`esc-monitor-timeline-dot esc-monitor-timeline-dot--${(
              event.timelineEventType || 'event'
            ).toLowerCase()}`}
          />
          <div>
            <p className="esc-monitor-timeline-title">
              {formatTimelineEventTitle(event)}
              {event.levelNumber ? ` · L${event.levelNumber}` : ''}
              {event.recipientRole ? ` · ${event.recipientRole}` : ''}
            </p>
            <p className="esc-monitor-timeline-message">{formatTimelineMessage(event)}</p>
            <p className="aat-timeline-meta">
              {event.recipientName ? `${event.recipientName}` : null}
              {event.recipientPhone ? ` · ${event.recipientPhone}` : null}
              {event.notificationChannel ? ` · ${event.notificationChannel}` : null}
              {event.deliveryStatus ? ` · ${event.deliveryStatus}` : null}
              {event.retryCount != null && event.retryCount > 0
                ? ` · Retry ${event.retryCount}`
                : null}
            </p>
            <p className="esc-monitor-timeline-time">{formatAuditTimestamp(event.timestamp)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}
